import React, { Component } from 'react';
import i18n from 'i18n-js';
import CDatePicker from 'components/custom/CDatePicker';
import { Segment, Grid, Form, Button, Checkbox, Icon, Popup } from 'semantic-grab-ui/src';
import formComponent from 'components/decorators/form_component';
import FormInputWithError from 'components/form/FormInputWithError';
import { frequencyList, MINUTE_FREQUENCY_INTERVALS } from 'data/trident-campaigns';
import { targetUserTypes } from 'data/trident-types';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import type { Country } from 'types/country';
import _ from 'lodash';
import moment from 'moment';
import Toastr from 'utils/toastr-util';
import './TreatmentForm.scss';

type Props = {
  model: TridentCampaignDetails,
  country: Country,
  canActivate: boolean,
  currentNode: string,
  parentNode: string,
  parentKeyList: Array<string>,
  onModelChange: (_?: Object) => void,
  handleCloseModal: Function,
  handleInputChange: Function,
  handleControlBlur: Function,
  isAdd: boolean,
  handleDeleteNode: Function,
  isValid: Function,
};

@formComponent()
export default class TimeTriggerForm extends Component {
  props: Props;
  constructor(props: Props) {
    super(props);
    this.state = {
      originalModel: this.props.model,
    };
  }

  componentDidMount() {
    // when creating schedule node, we make its start, end time same as the campaign by default
    if (this.props.isAdd) {
      this.handleCheckSameDatesChange({}, { checked: true });
    }
  }

  isEdit = (): boolean => {
    const hasCampaignID = _.get(this.props.model, 'campaignID', '');
    const isExistingNode = parseInt(this.props.currentNode, 10) <= parseInt(_.get(this.props.model, 'maxValueOfNodesKeyFromApi', 0), 10);
    return hasCampaignID && isExistingNode;
  };

  handleRepeatChange = (event: any, data: any) => {
    const { checked } = data;
    const updatedModel = _.cloneDeep(this.props.model);
    if (checked === true) {
      _.set(updatedModel, `nodes.${this.props.currentNode}.data.frequency`, '');
    } else {
      _.set(updatedModel, `nodes.${this.props.currentNode}.data.frequency`, 'nr');
    }
    this.props.onModelChange(updatedModel);
  };

  handleFrequencyChange = (event: any, data: any) => {
    const { currentNode, model } = this.props;
    const interval = _.get(model, `nodes.${currentNode}.data.interval`);
    if (data.value === 'mi' && !MINUTE_FREQUENCY_INTERVALS.includes(Number(interval))) {
      // only some intervals are allowed for minutes, so reset to a valid one in the same update
      const updatedModel = _.cloneDeep(model);
      _.set(updatedModel, `nodes.${currentNode}.data.interval`, 30);
      this.props.handleInputChange(event, data, updatedModel);
      return;
    }
    this.props.handleInputChange(event, data);
  };

  cancelTimeTrigger = () => {
    if (this.props.isAdd) {
      // eslint-disable-next-line no-alert
      const confirmValue = confirm('Cancel will delete this inactive node. Are you sure to delete this?');
      if (!confirmValue) return;
      this.props.handleDeleteNode(this.props.parentNode, this.props.currentNode, -1);
    } else {
      this.props.onModelChange(this.state.originalModel);
    }
    this.props.handleCloseModal();
  };

  saveTimeTrigger = () => {
    if (this.props.isValid()) {
      // check whether node name exists
      const nodeName = _.get(this.props.model, `nodes.${this.props.currentNode}.name`, '');
      const duplicateNameNode = _.find(this.props.model.nodes, (n, id) => n.name === nodeName && id !== this.props.currentNode);
      if (!_.isEmpty(nodeName) && !_.isEmpty(duplicateNameNode)) {
        Toastr.error(`Node name ${nodeName} is already used. Please use another name`);
        return;
      }
      this.props.handleCloseModal();
    } else {
      Toastr.error('There are errors in time form.');
    }
  };

  handleCheckSameDatesChange = (event: any, data: any) => {
    const { checked } = data;
    const { currentNode, model } = this.props;
    const updatedModel = _.cloneDeep(model);
    if (checked === true) {
      _.set(updatedModel, `nodes.${currentNode}.data.startTime`, updatedModel.startTime);
      _.set(updatedModel, `nodes.${currentNode}.data.endTime`, updatedModel.endTime);
    }
    _.set(updatedModel, `nodes.${currentNode}.data.isSameDateChecked`, checked);
    this.props.onModelChange(updatedModel);
  };

  render() {
    const { model, currentNode } = this.props;
    const frequencyTypesTranslated = frequencyList.map(({ text, value }) => ({ text: i18n.t(text), value }));
    const minuteIntervalOptions = MINUTE_FREQUENCY_INTERVALS.map(minutes => ({ key: minutes, text: String(minutes), value: minutes }));
    const monthlyFrequencyOptions = [];
    if (_.get(model.nodes[currentNode], 'data.startTime') !== '') {
      const dayOfMonth = moment(_.get(model.nodes[currentNode], 'data.startTime')).date();
      const week = Math.floor(dayOfMonth / 7);
      if (dayOfMonth <= 28) {
        monthlyFrequencyOptions.push({
          key: 0,
          value: 'day',
          text: `Monthly on day ${dayOfMonth}`,
        });
      }

      if (week < 4) {
        const dayName = moment(_.get(model.nodes[currentNode], 'data.startTime')).format('dddd');
        const weekNames = ['First', 'Second', 'Third', 'Fourth'];
        monthlyFrequencyOptions.push({
          key: 1,
          value: 'week',
          text: `Monthly on ${weekNames[week]} ${dayName}`,
        });
      }
    }
    const isRepeat = _.get(model.nodes[currentNode], 'data.frequency');
    const lastHappenedAt = _.get(model.nodes[currentNode], 'data.lastHappenedAt', '');
    const disableNextTriggerTime = isRepeat === 'nr' && lastHappenedAt;
    const autoHoldbackEnabled = _.get(model, `nodes.${currentNode}.autoHoldbackEnabled`, false);
    const sameDateChecked = _.get(model, `nodes.${currentNode}.data.isSameDateChecked`, false);

    if (!this.isEdit() && isRepeat === 'nr') {
      _.set(model.nodes[currentNode], 'data.endTime', model.endTime);
    }

    return (
      <div>
        <div className="model-button">
          <Grid>
            <Grid.Column textAlign="right">
              <Button.Group>
                <Button
                  onClick={this.cancelTimeTrigger}
                >
                  Cancel
                </Button>
                <Button.Or />
                <Button
                  positive
                  onClick={this.saveTimeTrigger}
                >
                  Save
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        </div>
        <div className="treatment-form">
          <Segment className="treatment-form-segment">
            <Grid>
              <Grid.Column width={12}>
                <Form>
                  <Grid>
                    <Grid.Column width={12}>
                      <Form>
                        <FormInputWithError
                          name={`nodes.${currentNode}.name`}
                          {...this.props}
                        >
                          <Form.Input
                            label="Time Trigger Name"
                            value={model.nodes[currentNode].name}
                            onChange={this.props.handleInputChange}
                          />
                        </FormInputWithError>
                      </Form>
                    </Grid.Column>
                  </Grid>
                  <Grid>
                    <Grid.Column width={4}>
                      <FormInputWithError
                        name={`nodes.${currentNode}.data.startTime`}
                        {...this.props}
                      >
                        <CDatePicker
                          required
                          disabled={this.isEdit() || sameDateChecked}
                          selectsStart
                          showTimeSelect
                          label={i18n.t('common.fields.startDate')}
                          minDate={model.startTime}
                          maxDate={model.endTime}
                          selected={_.get(model.nodes[currentNode], 'data.startTime')}
                          startDate={_.get(model.nodes[currentNode], 'data.startTime')}
                          endDate={_.get(model.nodes[currentNode], 'data.endTime')}
                          utcOffset={_.get(model, 'utcOffset')}
                          handleInputChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <FormInputWithError
                        name={`nodes.${currentNode}.data.endTime`}
                        {...this.props}
                      >
                        <CDatePicker
                          required
                          disabled={isRepeat === 'nr' || sameDateChecked}
                          selectsStart
                          showTimeSelect
                          label={i18n.t('common.fields.endDate')}
                          minDate={_.get(model.nodes[currentNode], 'data.startTime') || model.startTime}
                          maxDate={model.endTime}
                          selected={_.get(model.nodes[currentNode], 'data.endTime')}
                          startDate={_.get(model.nodes[currentNode], 'data.startTime')}
                          endDate={_.get(model.nodes[currentNode], 'data.endTime')}
                          utcOffset={_.get(model, 'utcOffset')}
                          handleInputChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Form.Field style={{ paddingTop: 30 }}>
                        <Checkbox
                          name="same_dates"
                          disabled={this.isEdit()}
                          label={i18n.t('targets_page.fields.same_as_campaign_dates')}
                          onChange={this.handleCheckSameDatesChange}
                          checked={sameDateChecked}
                        />
                      </Form.Field>
                    </Grid.Column>
                    {
                      (model.targetUserType === targetUserTypes.pax || model.targetUserType === targetUserTypes.dax) &&
                      <Grid.Column width={4}>
                        <Form.Field style={{ paddingTop: 30 }}>
                          <Checkbox
                            name={`nodes.${currentNode}.autoHoldbackEnabled`}
                            onChange={this.props.handleInputChange}
                            checked={autoHoldbackEnabled}
                            label="Auto Add Control Group"
                            disabled={!_.isEmpty(_.get(model, `nodes.${this.props.currentNode}.children`, []))}
                          />
                          <Popup
                            trigger={<Icon name="question circle outline" style={{ marginLeft: 3 }} />}
                            position="bottom left"
                            hoverable
                          >
                            <React.Fragment>
                              <p>This will allow Trident to create experiment node automatically with a Control Group (10%) & one Treatment Group (90%) split. You can learn further more at <a href="https://wiki.grab.com/pages/viewpage.action?pageId=326902276" target="_blank" rel="noopener noreferrer">HERE</a></p>
                            </React.Fragment>
                          </Popup>

                        </Form.Field>
                      </Grid.Column>
                    }
                  </Grid>
                </Form>
              </Grid.Column>
            </Grid>
            <Grid>

              <Grid.Column width={8}>
                <Form.Field>
                  <Checkbox
                    name="repeat"
                    label="Repeat"
                    onChange={this.handleRepeatChange}
                    checked={isRepeat !== 'nr'}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid>
            <Segment.Group vertical>
              <Segment>
                <Grid>
                  <Grid.Column width={6}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.interval`}
                      {...this.props}
                    >
                      {isRepeat === 'mi' ? (
                        <Form.Select
                          required
                          label={i18n.t('targets_page.fields.interval')}
                          options={minuteIntervalOptions}
                          value={_.get(model.nodes[currentNode], 'data.interval')}
                          onChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      ) : (
                        <Form.Input
                          required
                          type="number"
                          disabled={isRepeat === 'nr'}
                          label={i18n.t('targets_page.fields.interval')}
                          value={_.get(model.nodes[currentNode], 'data.interval')}
                          onChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      )}
                    </FormInputWithError>
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.frequency`}
                      {...this.props}
                    >
                      <Form.Select
                        label={i18n.t('targets_page.fields.frequency')}
                        options={frequencyTypesTranslated}
                        value={_.get(model.nodes[currentNode], 'data.frequency')}
                        onChange={this.handleFrequencyChange}
                        onBlur={this.props.handleControlBlur}
                        disabled={isRepeat === 'nr'}
                      />
                    </FormInputWithError>
                  </Grid.Column>
                </Grid>
              </Segment>
              <Segment>
                <Grid>
                  <Grid.Column width={6}>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.monthlyFrequency`}
                      {...this.props}
                    >
                      <Form.Select
                        disabled={_.get(model.nodes[currentNode], 'data.frequency') !== 'mt'}
                        label={i18n.t('targets_page.fields.monthlyFrequency')}
                        options={monthlyFrequencyOptions}
                        value={_.get(model.nodes[currentNode], 'data.monthlyFrequency')}
                        onChange={this.props.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Grid.Column>
                </Grid>
              </Segment>
            </Segment.Group>
            {this.isEdit() &&
              <Segment>
                <Form>
                  <Grid>
                    <Grid.Column width={6}>
                      <FormInputWithError
                        name={`nodes.${currentNode}.data.nextTriggerTime`}
                        {...this.props}
                      >
                        <CDatePicker
                          showTimeSelect
                          label={i18n.t('targets_page.fields.nextTriggerTime')}
                          minDate={_.get(model.nodes[currentNode], 'data.startTime') || model.startTime}
                          maxDate={_.get(model.nodes[currentNode], 'data.endTime') || model.endTime}
                          selected={_.get(model.nodes[currentNode], 'data.nextTriggerTime')}
                          utcOffset={_.get(model, 'utcOffset')}
                          handleInputChange={this.props.handleInputChange}
                          disabled={disableNextTriggerTime}
                          onBlur={this.props.handleControlBlur}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <FormInputWithError
                        name={`nodes.${currentNode}.data.lastHappenedAt`}
                        {...this.props}
                      >
                        <CDatePicker
                          showTimeSelect
                          disabled
                          label={i18n.t('targets_page.fields.lastHappenedAt')}
                          selected={lastHappenedAt}
                          utcOffset={_.get(model, 'utcOffset')}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                  </Grid>
                </Form>
              </Segment>
            }
          </Segment>
        </div>
      </div>
    );
  }
}
